import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Paperclip, Wallet, Clock, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

/** Bandeja de pagos reportados por las familias: se ve el comprobante y se aprueba o rechaza. */
export default function AdminPagosAprobar() {
  const [datos, setDatos] = useState(null);
  const [rechazo, setRechazo] = useState(null);
  const [procesando, setProcesando] = useState(null);

  const cargar = () => api.get("/pagos/por-aprobar").then((r) => setDatos(r.data))
    .catch(() => setDatos({ pagos: [], cantidad: 0, monto: 0 }));

  useEffect(() => { cargar(); }, []);

  const verComprobante = async (f) => {
    try {
      const { data } = await api.get(`/facturas/${f.id}/comprobante-pago`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (e) {
      toast.error(apiError(e, "Esta factura no tiene comprobante adjunto"));
    }
  };

  const aprobar = async (f) => {
    setProcesando(f.id);
    try {
      await api.post(`/facturas/${f.id}/confirmar-pago`, { metodo: f.metodo_pago });
      toast.success(`Pago de ${f.ncf} aprobado`, {
        description: `${money(f.monto_total)} · se notificó a ${f.tutor_nombre || "la familia"}`,
      });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
    setProcesando(null);
  };

  const confirmarRechazo = async () => {
    setProcesando(rechazo.id);
    try {
      await api.post(`/facturas/${rechazo.id}/rechazar-pago`, { motivo: rechazo.motivo });
      toast.success("Pago rechazado y familia notificada");
      setRechazo(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
    setProcesando(null);
  };

  if (!datos) return <Loading />;

  return (
    <div data-testid="admin-pagos-aprobar">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitle title="Pagos por aprobar"
                   subtitle="Pagos reportados por las familias: revise el comprobante y apruebe o rechace." />
        <Button variant="outline" className="rounded-full" onClick={cargar} data-testid="refrescar-pagos">
          <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
        </Button>
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        <Stat label="Pagos esperando" value={datos.cantidad} icon={Clock}
              tone={datos.cantidad ? "warn" : "default"} testId="pagos-pendientes" />
        <Stat label="Monto reportado" value={money(datos.monto)} icon={Wallet}
              testId="pagos-monto" />
      </div>

      <div className="space-y-3" data-testid="lista-pagos-aprobar">
        {datos.pagos.map((f) => (
          <article key={f.id} className="lift rounded-lg border border-border/60 bg-card p-5">
            <div className="flex flex-wrap items-start gap-4">
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-base font-semibold">
                  {f.cliente_nombre || f.tutor_nombre || "—"}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">{f.ncf}</span>
                </h3>
                <p className="text-sm text-muted-foreground">
                  {f.estudiante_nombre} · {f.concepto}
                </p>
                <p className="text-xs text-muted-foreground">
                  Reportado {f.comprobante_subido_en ? fecha(f.comprobante_subido_en) : "—"}
                  {" · "}{f.metodo_pago || "transferencia"}
                  {f.referencia_pago ? ` · Ref. ${f.referencia_pago}` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="font-display text-lg font-bold" data-testid={`monto-${f.ncf}`}>
                  {money(f.monto_total)}
                </p>
                <Badge variant={f.tiene_comprobante ? "secondary" : "outline"}>
                  {f.tiene_comprobante ? "con comprobante" : "sin comprobante"}
                </Badge>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full"
                      disabled={!f.tiene_comprobante} onClick={() => verComprobante(f)}
                      data-testid={`ver-captura-${f.ncf}`}>
                <Paperclip className="mr-1 h-3 w-3" /> Ver comprobante
              </Button>
              <Button size="sm" className="rounded-full" disabled={procesando === f.id}
                      onClick={() => aprobar(f)} data-testid={`aprobar-${f.ncf}`}>
                <CheckCircle2 className="mr-1 h-3 w-3" /> Aprobar pago
              </Button>
              <Button size="sm" variant="outline" className="rounded-full"
                      onClick={() => setRechazo({ ...f, motivo: "" })}
                      data-testid={`rechazar-${f.ncf}`}>
                <XCircle className="mr-1 h-3 w-3 text-destructive" /> Rechazar
              </Button>
            </div>
          </article>
        ))}
        {datos.pagos.length === 0 && (
          <Empty testId="pagos-aprobar-vacios">
            No hay pagos esperando aprobación. Cuando una familia reporte un pago desde su portal
            aparecerá aquí con su comprobante.
          </Empty>
        )}
      </div>

      <Dialog open={!!rechazo} onOpenChange={(o) => !o && setRechazo(null)}>
        <DialogContent data-testid="modal-rechazo">
          <DialogHeader>
            <DialogTitle className="font-display">Rechazar el pago de {rechazo?.ncf}</DialogTitle>
            <DialogDescription>
              La familia recibirá el motivo y podrá reportar el pago de nuevo.
            </DialogDescription>
          </DialogHeader>
          {rechazo && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Input value={rechazo.motivo} data-testid="motivo-rechazo"
                       placeholder="El comprobante no coincide con el monto de la factura"
                       onChange={(e) => setRechazo({ ...rechazo, motivo: e.target.value })} />
              </div>
              <Button className="rounded-full py-6" onClick={confirmarRechazo}
                      disabled={procesando === rechazo.id} data-testid="confirmar-rechazo">
                Rechazar y notificar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
